import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Plus, Minus, ArrowLeft, ArrowRight, MapPin, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = ["Véhicules", "Adresse", "Créneau", "Coordonnées", "Récapitulatif"];

const CATEGORY_LABELS = { citadine: "Citadine / Compacte", berline: "Berline", suv: "SUV (5 places)", grand_van: "Grand Van (8-9 places)" };
const FORMULA_LABELS = {
  coup_de_propre: "Coup de propre intérieur",
  interieur_approfondi: "Intérieur approfondi",
  lavage_exterieur: "Lavage extérieur",
  complet_entretien: "Complet entretien",
};

export default function AutoCleaningPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [vehicles, setVehicles] = useState([{ category: "berline", formula: "formule_classique", quantity: 1 }]);
  const [veryDirty, setVeryDirty] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [address, setAddress] = useState("");
  const [addressDetails, setAddressDetails] = useState("");
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [desiredDate, setDesiredDate] = useState("");
  const [desiredTime, setDesiredTime] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientSurname, setClientSurname] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [comments, setComments] = useState("");
  const [priceResult, setPriceResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/property/catalog`).then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const addVehicle = () => setVehicles(prev => [...prev, { category: "berline", formula: "interieur_premium", quantity: 1 }]);
  const removeVehicle = (i) => setVehicles(prev => prev.filter((_, idx) => idx !== i));
  const updateVehicle = (i, field, value) => setVehicles(prev => prev.map((v, idx) => idx === i ? { ...v, [field]: value } : v));

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) { toast.error("Maximum 5 photos"); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => { reader.onload = () => resolve(reader.result); reader.readAsDataURL(file); });
        const { data } = await axios.post(`${API}/api/property/upload-photo`, { file_data: base64, file_name: file.name });
        if (data.success) setPhotos(prev => [...prev, { url: data.url, name: file.name }]);
      } catch { toast.error(`Erreur upload ${file.name}`); }
    }
    setUploading(false);
  };

  const calculateDistance = useCallback(async () => {
    if (!address || address.length < 5) return;
    try {
      const { data } = await axios.get(`${API}/api/property/distance`, { params: { address } });
      setDistanceInfo(data);
    } catch {
      setDistanceInfo(null);
    }
  }, [address]);

  const calculatePrice = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/property/calculate-price`, {
        service_type: "auto",
        client_name: clientName || "Preview",
        client_email: clientEmail || "preview@test.com",
        client_phone: clientPhone || "+33000000000",
        address,
        vehicles: vehicles.map(v => ({ category: v.category, formula: v.formula, quantity: v.quantity })),
        very_dirty: veryDirty,
        selected_options: selectedOptions,
        distance_km: distanceInfo?.distance_km || null,
      });
      setPriceResult(data);
    } catch {
      toast.error("Erreur lors du calcul du tarif");
    } finally {
      setLoading(false);
    }
  }, [vehicles, veryDirty, selectedOptions, address, distanceInfo, clientName, clientEmail, clientPhone]);

  useEffect(() => {
    if (step === 4) calculatePrice();
  }, [step, calculatePrice]);

  const getUnitPrice = (cat, formula) => {
    if (!catalog || cat === "camion") return null;
    return catalog.auto.prices[cat]?.[formula] || null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/property/request`, {
        service_type: "auto",
        client_name: clientName,
        client_surname: clientSurname,
        client_email: clientEmail,
        client_phone: clientPhone,
        address,
        address_details: addressDetails,
        desired_date: desiredDate,
        desired_time: desiredTime,
        vehicles: vehicles.map(v => ({ category: v.category, formula: v.formula, quantity: v.quantity })),
        very_dirty: veryDirty,
        selected_options: selectedOptions,
        distance_km: distanceInfo?.distance_km || null,
        comments,
        photo_urls: photos.map(p => p.url),
      });
      if (data.success) {
        setWhatsappLink(data.whatsapp_link || "");
        setSubmitted(true);
        toast.success("Demande envoyée avec succès !");
      }
    } catch {
      toast.error("Erreur lors de l'envoi de votre demande");
    } finally {
      setSubmitting(false);
    }
  };

  const canGoNext = () => {
    if (step === 0) return vehicles.length > 0 && vehicles.every(v => v.category && v.formula);
    if (step === 1) return address.length >= 5;
    if (step === 2) return true;
    if (step === 3) return clientName && clientEmail && clientPhone;
    return true;
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="auto-success">
        <div className="max-w-md text-center">
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Demande envoyée</h1>
          <p className="text-[#1A1A1A]/60 text-sm leading-relaxed mb-6">
            Nous avons bien reçu votre demande de nettoyage automobile. Notre équipe vous recontactera très rapidement pour confirmer votre intervention.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/property-services")} className="bg-[#1A1A1A] text-white px-6 py-3 text-xs tracking-[0.15em] uppercase">
              Retour aux services
            </button>
            {whatsappLink && (
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                className="border border-[#1A1A1A]/20 text-[#1A1A1A] px-6 py-3 text-xs tracking-[0.15em] uppercase hover:border-[#1A1A1A]/50 transition-colors"
                data-testid="whatsapp-link">
                Nous contacter sur WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO title="Nettoyage Automobile — ALTURA Property Services" description="Nettoyage intérieur et extérieur de votre véhicule à domicile. Tarification transparente." />

      <section className="bg-[#1A1A1A] py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <button onClick={() => navigate("/property-services")} className="text-white/50 text-xs flex items-center gap-2 mb-6 hover:text-white">
            <ArrowLeft size={12} /> Property Services
          </button>
          <h1 className="font-heading text-2xl sm:text-3xl text-white tracking-wide" data-testid="auto-title">
            Nettoyage Automobile
          </h1>
          {/* Steps indicator */}
          <div className="flex gap-1 mt-6">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1 ${i <= step ? "bg-white" : "bg-white/20"} transition-colors`} />
                <p className={`text-[10px] mt-1 ${i <= step ? "text-white/80" : "text-white/30"}`}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#EDEBE4] py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-6">

          {/* STEP 0: Vehicles */}
          {step === 0 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-vehicles">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Vos véhicules</h2>
              {vehicles.map((v, i) => (
                <div key={i} className="bg-white border border-[#1A1A1A]/10 p-5 mb-4" data-testid={`vehicle-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[#1A1A1A] text-sm font-medium">Véhicule {i + 1}</p>
                    {vehicles.length > 1 && (
                      <button onClick={() => removeVehicle(i)} className="text-red-500 text-xs hover:text-red-700">Supprimer</button>
                    )}
                  </div>
                  {/* Category */}
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Catégorie</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <button key={key} onClick={() => updateVehicle(i, "category", key)}
                        className={`p-3 text-xs border transition-colors ${v.category === key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#1A1A1A]/20 hover:border-[#1A1A1A]/50"}`}
                        data-testid={`cat-${key}-${i}`}
                      >{label}</button>
                    ))}
                  </div>
                  {/* Formula - split by group */}
                  {catalog && (() => {
                    const uniteFormulas = Object.entries(catalog.auto.formulas).filter(([_, f]) => f.group === "unite");
                    const completeFormulas = Object.entries(catalog.auto.formulas).filter(([_, f]) => f.group === "complete");
                    const renderBtn = ([key, info]) => {
                      const price = getUnitPrice(v.category, key);
                      const duration = info.durations?.[v.category] || "";
                      return (
                        <button key={key} onClick={() => updateVehicle(i, "formula", key)}
                          className={`w-full text-left p-3 border transition-colors ${v.formula === key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#1A1A1A]/20 hover:border-[#1A1A1A]/50"}`}
                          data-testid={`formula-${key}-${i}`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium">{info.name}</p>
                              <p className={`text-xs mt-0.5 ${v.formula === key ? "text-white/60" : "text-[#1A1A1A]/50"}`}>{info.detail}{duration ? ` — ${duration}` : ""}</p>
                            </div>
                            {price != null && <p className="text-sm font-medium ml-4 whitespace-nowrap">{price} €</p>}
                          </div>
                        </button>
                      );
                    };
                    return (
                      <>
                        <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Prestations à l'unité</label>
                        <div className="space-y-2 mb-4">{uniteFormulas.map(renderBtn)}</div>
                        <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Formules complètes</label>
                        <div className="space-y-2 mb-4">{completeFormulas.map(renderBtn)}</div>
                      </>
                    );
                  })()}
                  {/* Quantity */}
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Nombre de véhicules identiques</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => updateVehicle(i, "quantity", Math.max(1, v.quantity - 1))} className="w-8 h-8 border border-[#1A1A1A]/20 flex items-center justify-center"><Minus size={14} /></button>
                    <span className="text-[#1A1A1A] font-medium w-8 text-center">{v.quantity}</span>
                    <button onClick={() => updateVehicle(i, "quantity", v.quantity + 1)} className="w-8 h-8 border border-[#1A1A1A]/20 flex items-center justify-center"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={addVehicle} className="flex items-center gap-2 text-[#1A1A1A]/60 text-xs hover:text-[#1A1A1A] mt-2" data-testid="add-vehicle">
                <Plus size={14} /> Ajouter un véhicule
              </button>

              {/* Very dirty option */}
              <label className="flex items-center gap-3 mt-6 cursor-pointer" data-testid="very-dirty-toggle">
                <input type="checkbox" checked={veryDirty} onChange={e => setVeryDirty(e.target.checked)}
                  className="w-4 h-4 accent-[#1A1A1A]" />
                <span className="text-[#1A1A1A]/70 text-sm">Véhicule très sale / poils d'animaux (supplément sur devis)</span>
              </label>

              {/* Options à la carte */}
              {catalog?.auto?.options && (
                <div className="mt-6">
                  <p className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide mb-3">Options à la carte (supplément)</p>
                  <div className="space-y-2">
                    {Object.entries(catalog.auto.options).map(([key, opt]) => (
                      <label key={key} className="flex items-start gap-3 cursor-pointer p-3 border border-[#1A1A1A]/10 hover:border-[#1A1A1A]/20 transition-colors" data-testid={`option-${key}`}>
                        <input type="checkbox"
                          checked={selectedOptions.includes(key)}
                          onChange={e => {
                            if (e.target.checked) setSelectedOptions(prev => [...prev, key]);
                            else setSelectedOptions(prev => prev.filter(k => k !== key));
                          }}
                          className="w-4 h-4 accent-[#1A1A1A] mt-0.5" />
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="text-[#1A1A1A] text-sm">{opt.name}</span>
                            <span className="text-[#1A1A1A]/60 text-sm ml-2 whitespace-nowrap">{opt.price_min}-{opt.price_max} €</span>
                          </div>
                          <span className="text-[#1A1A1A]/40 text-xs">{opt.duration}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 1: Address */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-address">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Adresse d'intervention</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Adresse complète</label>
                  <input type="text" value={address} onChange={e => setAddress(e.target.value)} onBlur={calculateDistance}
                    placeholder="123 Rue de Paris, 78000 Versailles"
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                    data-testid="address-input" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Précisions (optionnel)</label>
                  <input type="text" value={addressDetails} onChange={e => setAddressDetails(e.target.value)}
                    placeholder="Code d'accès, parking, étage..."
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none" />
                </div>
                {distanceInfo && (
                  <div className={`p-4 ${distanceInfo.sur_devis ? "bg-yellow-50 border-yellow-200" : "bg-[#EDEBE4]"} border`} data-testid="distance-result">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin size={14} className="text-[#1A1A1A]/50" />
                      <p className="text-sm text-[#1A1A1A]">{distanceInfo.distance_km} km depuis notre base</p>
                    </div>
                    {distanceInfo.sur_devis ? (
                      <p className="text-xs text-yellow-700">Au-delà de 50 km — frais de déplacement sur devis</p>
                    ) : (
                      <p className="text-xs text-[#1A1A1A]/60">
                        Frais de déplacement : {distanceInfo.travel_cost === 0 ? "Gratuit" : `${distanceInfo.travel_cost} €`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Date/Time */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-schedule">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Créneau souhaité</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Date souhaitée</label>
                  <input type="date" value={desiredDate} onChange={e => setDesiredDate(e.target.value)}
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                    data-testid="date-input" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Heure souhaitée</label>
                  <input type="time" value={desiredTime} onChange={e => setDesiredTime(e.target.value)}
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                    data-testid="time-input" />
                </div>
                <p className="text-[#1A1A1A]/50 text-xs">Le créneau définitif sera confirmé par notre équipe après validation.</p>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Contact info */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-contact">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Vos coordonnées</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Prénom *</label>
                    <input type="text" value={clientName} onChange={e => setClientName(e.target.value)}
                      className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                      data-testid="name-input" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Nom</label>
                    <input type="text" value={clientSurname} onChange={e => setClientSurname(e.target.value)}
                      className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Email *</label>
                  <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)}
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                    data-testid="email-input" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Téléphone *</label>
                  <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)}
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none"
                    data-testid="phone-input" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Commentaires ou demandes particulières</label>
                  <textarea value={comments} onChange={e => setComments(e.target.value)} rows={3}
                    placeholder="Vous avez une demande particulière ou des détails à ajouter concernant votre intervention ? Indiquez-les ici."
                    className="w-full p-3 border border-[#1A1A1A]/20 text-sm bg-transparent focus:border-[#1A1A1A] outline-none resize-none"
                    data-testid="comments-input" />
                </div>
                {/* Photo upload */}
                <div>
                  <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Photos (optionnel — max 5)</label>
                  <p className="text-[#1A1A1A]/40 text-xs mb-2">Ajoutez des photos de votre véhicule pour un devis plus précis.</p>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handlePhotoUpload}
                    className="text-xs text-[#1A1A1A]/60" disabled={uploading || photos.length >= 5} data-testid="photo-upload" />
                  {uploading && <p className="text-xs text-[#1A1A1A]/50 mt-1">Upload en cours...</p>}
                  {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {photos.map((p, i) => (
                        <div key={i} className="relative w-16 h-16 border border-[#1A1A1A]/10 overflow-hidden">
                          <img src={`${API}${p.url}`} alt={p.name} className="w-full h-full object-cover" />
                          <button onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                            className="absolute top-0 right-0 bg-red-500 text-white text-[8px] w-4 h-4 flex items-center justify-center">x</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Summary */}
          {step === 4 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} data-testid="step-summary">
              <h2 className="font-heading text-lg text-[#1A1A1A] mb-6">Récapitulatif de votre demande</h2>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 space-y-4">
                {/* Client */}
                <div>
                  <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Client</p>
                  <p className="text-[#1A1A1A] text-sm">{clientName} {clientSurname}</p>
                  <p className="text-[#1A1A1A]/60 text-sm">{clientPhone} — {clientEmail}</p>
                </div>
                {/* Address */}
                <div>
                  <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Adresse</p>
                  <p className="text-[#1A1A1A] text-sm">{address}</p>
                  {addressDetails && <p className="text-[#1A1A1A]/60 text-xs">{addressDetails}</p>}
                </div>
                {/* Date */}
                {desiredDate && (
                  <div>
                    <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Créneau souhaité</p>
                    <p className="text-[#1A1A1A] text-sm">{desiredDate} {desiredTime && `à ${desiredTime}`}</p>
                  </div>
                )}
                {/* Breakdown */}
                <hr className="border-[#D0CEC7]" />
                {loading ? (
                  <div className="flex items-center gap-2 py-4"><Spinner size={16} className="animate-spin" /> <span className="text-sm text-[#1A1A1A]/50">Calcul en cours...</span></div>
                ) : priceResult && (
                  <>
                    <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide">Détail de la demande</p>
                    <div className="space-y-2">
                      {priceResult.breakdown.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#1A1A1A]">
                            {item.label}
                            {item.starting_from && <span className="text-[#1A1A1A]/50 text-xs ml-1">(à partir de)</span>}
                          </span>
                          <span className="text-[#1A1A1A] font-medium">
                            {item.amount > 0 ? `${item.amount} €` : "Sur devis"}
                          </span>
                        </div>
                      ))}
                    </div>
                    <hr className="border-[#D0CEC7]" />
                    <div className="flex justify-between items-center">
                      <span className="text-[#1A1A1A] font-heading text-lg">TOTAL</span>
                      <span className="text-[#1A1A1A] font-heading text-lg" data-testid="total-amount">
                        {priceResult.sur_devis ? `${priceResult.total > 0 ? `À partir de ${priceResult.total} €` : "Sur devis"}` : `${priceResult.total} €`}
                      </span>
                    </div>
                    {priceResult.sur_devis && (
                      <p className="text-yellow-700 text-xs bg-yellow-50 p-3 border border-yellow-200">
                        Ce tarif est indicatif. Le montant final sera confirmé par notre équipe après étude de votre demande.
                      </p>
                    )}
                  </>
                )}
                {comments && (
                  <div>
                    <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Commentaires</p>
                    <p className="text-[#1A1A1A]/70 text-sm">{comments}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-[#1A1A1A]/60 text-xs hover:text-[#1A1A1A] uppercase tracking-wide">
                <ArrowLeft size={12} /> Précédent
              </button>
            ) : <div />}
            {step < 4 ? (
              <button onClick={() => canGoNext() && setStep(s => s + 1)} disabled={!canGoNext()}
                className={`flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-wide transition-colors ${canGoNext() ? "bg-[#1A1A1A] text-white hover:bg-[#333]" : "bg-[#1A1A1A]/20 text-[#1A1A1A]/40 cursor-not-allowed"}`}
                data-testid="next-btn"
              >
                Suivant <ArrowRight size={12} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting}
                className="bg-[#1A1A1A] text-white px-8 py-3 text-xs uppercase tracking-[0.15em] hover:bg-[#333] transition-colors disabled:opacity-50"
                data-testid="submit-btn"
              >
                {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
